import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import api from "../../services/api";
import { toast } from "react-toastify";
import useWrapper from "../../hooks/useWrapper";
import useCostCenter from "../../hooks/api/useCostCenter";
import useFormat from "../../hooks/utils/useFormat";
import { Currency, InputText, IntegerNumber } from "../Input";
import Button from "../Button";
import Slct from "../Slct";
import Results from "../Results";
import ModalCustom from "./modal-template-default";
import Required from "../Required";
import { validateForm } from "../ValidateForm";
import usePermission from "../../hooks/usePermission";

/**
 * Lança um Item Adicional (contratado) para o formando, fora do modal grande de
 * renegociação — que cancelaria e recriaria todo o financeiro.
 *
 * Cria apenas o registro em `custom_payments`. Os boletos correspondentes são
 * gerados no passo seguinte, pelo modal "Gerar Diferença" (que a página abre
 * automaticamente via refreshCallback), para que nasçam como "Contratado".
 */
const ModalAdditionalItem = props => {
  const { show, onHide, trainee, events = [], refreshCallback } = props;
  const { config } = useWrapper();
  const { formatReal } = useFormat();
  const canCreate = usePermission("financialGroup", "create");
  const costCenter = useCostCenter({ query: "?type=receita" });

  const [selects, setselects] = useState({});
  const [name, setname] = useState("");
  const [description, setdescription] = useState("");
  const [quantity, setquantity] = useState(1);
  const [value, setvalue] = useState();
  const [convenienceFee, setconvenienceFee] = useState();
  const [isWaitingResponse, setisWaitingResponse] = useState(false);

  useEffect(() => {
    if (show) {
      setselects({});
      setname("");
      setdescription("");
      setquantity(1);
      setvalue();
      setconvenienceFee();
    }
  }, [show]);

  const closeModal = () => {
    if (isWaitingResponse) return;
    onHide();
  };

  const total = (Number(quantity) || 0) * (value || 0) + (convenienceFee || 0);

  const saveItem = async () => {
    if (!canCreate) return;
    const _is_valid = validateForm(document.getElementById("additional_item_container"));
    if (!_is_valid) return;
    if (!(total > 0)) {
      toast.error("O valor total do item precisa ser maior que zero.");
      return;
    }

    setisWaitingResponse(true);
    try {
      await api.post(
        "custom-payment",
        {
          payer_type: "item_adicional",
          trainees: [trainee?.id],
          commercial_id: trainee?.commercial_id,
          cost_center_id: selects?.cost_center_id,
          expense_plan_id: selects?.expense_plan_id,
          expense_id: selects?.expense_id,
          event_id: selects?.event_id || null,
          resource_id: selects?.resource_id || null,
          name,
          description: description || null,
          quantity: Number(quantity) || 1,
          value: value || 0,
          // nunca null: em MySQL (quantity * value + NULL) = NULL, o que fazia
          // o item sumir da soma de itens adicionais do formando
          convenience_fee: convenienceFee || 0,
          disabled: false,
        },
        config,
      );
      toast.success("Item adicional cadastrado!");
      onHide();
      refreshCallback && refreshCallback();
    } catch (__err) {
      const _message = __err?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao cadastrar o item adicional!");
      console.error("__err", __err);
    } finally {
      setisWaitingResponse(false);
    }
  };

  const _selected_event = events?.find(__event => __event.value === selects?.event_id);

  return (
    <ModalCustom
      show={show}
      onHide={closeModal}
      id="additional_item_container"
      size="lg"
      disableClose={isWaitingResponse}
    >
      <ModalCustom.Header>
        <ModalCustom.Title>Adicionar Item Adicional</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        <p className="small-label mb-2">
          O item entra no valor contratado do formando e <b>aumenta</b> o que ele deve. Ao salvar, você poderá gerar os
          boletos da diferença, sem interferir nos pagamentos já negociados.
        </p>
        <Row className="mt-2">
          <Col sm={4}>
            <span>
              Centro de Custo <Required />
            </span>
            <Slct
              required={true}
              isDisabled={isWaitingResponse}
              options={costCenter || []}
              value={selects?.cost_center_id}
              onChange={__ev =>
                setselects({ ...selects, cost_center_id: __ev.value, expense_plan_id: null, expense_id: null })
              }
              placeholder="..."
            />
          </Col>
          <Col sm={4}>
            <span>
              Plano de Conta <Required />
            </span>
            <Slct
              required={true}
              isDisabled={isWaitingResponse}
              options={
                selects?.cost_center_id
                  ? costCenter?.find(__cost_center => __cost_center.value === selects.cost_center_id)?.expensePlan || []
                  : []
              }
              value={selects?.expense_plan_id}
              onChange={__ev => setselects({ ...selects, expense_plan_id: __ev.value, expense_id: null })}
              placeholder="..."
            />
          </Col>
          <Col sm={4}>
            <span>
              Conta <Required />
            </span>
            <Slct
              required={true}
              isDisabled={isWaitingResponse}
              options={
                selects?.expense_plan_id
                  ? costCenter
                      ?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                      ?.expensePlan?.find(__expense_plan => __expense_plan.value === selects.expense_plan_id)
                      ?.expenses || []
                  : []
              }
              value={selects?.expense_id}
              onChange={__ev => setselects({ ...selects, expense_id: __ev.value })}
              placeholder="..."
            />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col sm={5}>
            <span>
              Nome do Item <Required />
            </span>
            <InputText
              required={true}
              disabled={isWaitingResponse}
              value={name}
              onChange={__ev => setname(__ev.target.value)}
              placeholder="Ex: Camiseta da turma"
            />
          </Col>
          <Col sm={2}>
            <span>
              Quantidade <Required />
            </span>
            <IntegerNumber
              required={true}
              disabled={isWaitingResponse}
              allowNegative={false}
              value={quantity}
              valueChange={__ev => setquantity(__ev)}
              onBlur={__ev => {
                if (!(__ev.target?.value > 0)) setquantity(1);
              }}
              placeholder="1"
            />
          </Col>
          <Col sm={2}>
            <span>
              Valor Unitário <Required />
            </span>
            <Currency
              required={true}
              allowNegative={false}
              disabled={isWaitingResponse}
              value={value}
              valueChange={__ev => setvalue(__ev)}
            />
          </Col>
          <Col sm={3}>
            <span>Taxa de Conveniência</span>
            <Currency
              allowNegative={false}
              disabled={isWaitingResponse}
              value={convenienceFee}
              valueChange={__ev => setconvenienceFee(__ev)}
            />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col sm={4}>
            <span>Evento</span>
            <Slct
              isDisabled={isWaitingResponse}
              options={events || []}
              value={selects?.event_id}
              onChange={__ev => setselects({ ...selects, event_id: __ev.value, resource_id: null })}
              placeholder="..."
            />
          </Col>
          <Col sm={4}>
            <span>Recurso</span>
            <Slct
              isDisabled={isWaitingResponse || !selects?.event_id}
              options={_selected_event?.resources || []}
              value={selects?.resource_id}
              onChange={__ev => setselects({ ...selects, resource_id: __ev.value })}
              placeholder="..."
            />
          </Col>
          <Col sm={4}>
            <span>Descrição</span>
            <InputText
              disabled={isWaitingResponse}
              value={description}
              onChange={__ev => setdescription(__ev.target.value)}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm={6}></Col>
          <Col sm={6}>
            <Results
              rows={[
                {
                  label: `Valor Unitário × ${Number(quantity) || 0}`,
                  value: formatReal((Number(quantity) || 0) * (value || 0)),
                  nowrap: true,
                },
                { label: "Taxa de Conveniência", value: formatReal(convenienceFee || 0), nowrap: true },
                {
                  label: <b>Total a Acrescentar</b>,
                  value: <b>{formatReal(total)}</b>,
                  type: "dark",
                  nowrap: true,
                },
              ]}
            />
          </Col>
        </Row>
      </ModalCustom.Body>
      <ModalCustom.Footer>
        <Button disabled={isWaitingResponse} color="primary_ghost" onClick={closeModal}>
          Cancelar
        </Button>
        <Button disabled={isWaitingResponse || !canCreate} onClick={saveItem} loading={isWaitingResponse}>
          Adicionar Item
        </Button>
      </ModalCustom.Footer>
    </ModalCustom>
  );
};

export default ModalAdditionalItem;
